import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Leaf, ShieldCheck, LineChart as LineChartIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/807655ec-9c1c-4fef-ab7a-1bb7a6064990/images/ffa5ca17b63e2ea0dd85fee8f111467cf2382626ac5855a1964ca79ab8c15505.png";

export default function GuestLogin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Auto-redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate("/app/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const enterAsGuest = () => {
    localStorage.setItem("mag_guest", "1");
    navigate("/app/dashboard");
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F8F6]">
      {/* Left: Image side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={BG_IMG}
          alt="Prodotti freschi da negozio di alimentari italiano"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2E4A3B]/70 via-[#2E4A3B]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/25">
              <Leaf size={22} />
            </div>
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
              In<span className="text-[#A8D4B8]">Palmo</span>
            </span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4 max-w-md"
          >
            <h1 className="text-4xl lg:text-5xl font-semibold leading-tight tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Il tuo magazzino,<br />in palmo di mano.
            </h1>
            <p className="text-base text-white/90 leading-relaxed">
              Gestisci prodotti freschi e confezionati, ricorda lotti e scadenze, scopri cosa vende di più — tutto in un posto solo.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-10 flex items-center gap-2 lg:hidden">
            <div className="h-10 w-10 rounded-xl bg-[#2E4A3B] flex items-center justify-center">
              <Leaf size={22} className="text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
              In<span className="text-[#2E4A3B]">Palmo</span>
            </span>
          </div>

          <span className="inline-block text-xs font-semibold uppercase tracking-[0.12em] text-[#3B7A57] bg-[#E8F3EC] border border-[#C5E1D1] px-3 py-1 rounded-md mb-6">
            Accesso
          </span>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Benvenuto
          </h2>
          <p className="text-[#5C5F5A] text-base leading-relaxed mb-10">
            Entra con Google per salvare i tuoi dati in cloud, oppure prova la demo come ospite.
          </p>

          <div className="space-y-3 mb-8">
            <Feature icon={<ShieldCheck size={18} />} text="Alert automatici per scadenze e stock basso" />
            <Feature icon={<LineChartIcon size={18} />} text="Dashboard vendite con grafici e storico" />
            <Feature icon={<Leaf size={18} />} text="Import Excel/CSV per caricare dati velocemente" />
          </div>

          <Button
            data-testid="google-login-btn"
            onClick={signInWithGoogle}
            className="w-full h-14 text-base bg-[#2E4A3B] hover:bg-[#21382C] text-white rounded-lg font-medium"
          >
            <GoogleIcon />
            Accedi con Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#EAE7E1]" />
            <span className="text-xs uppercase tracking-wider text-[#8E918C]">oppure</span>
            <div className="flex-1 h-px bg-[#EAE7E1]" />
          </div>

          <Button
            data-testid="guest-login-btn"
            onClick={enterAsGuest}
            variant="outline"
            className="w-full h-12 text-sm bg-white border-[#EAE7E1] hover:bg-[#F4F1ED] text-[#2E4A3B] rounded-lg font-medium"
          >
            Entra come Ospite (demo)
          </Button>

          <p className="text-xs text-[#8E918C] text-center mt-6">
            La modalità ospite usa dati demo condivisi e non richiede registrazione.
          </p>

          <div className="mt-6 pt-4 border-t border-[#EAE7E1] flex items-center justify-center gap-4 text-[11px] text-[#8E918C]">
            <Link to="/privacy" className="hover:text-[#2E4A3B]" data-testid="entra-privacy-link">
              Privacy
            </Link>
            <span>·</span>
            <Link to="/termini" className="hover:text-[#2E4A3B]" data-testid="entra-terms-link">
              Termini
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm text-[#1A1C1A]">
      <div className="h-8 w-8 rounded-lg bg-[#E8F3EC] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <span>{text}</span>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" className="mr-3" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.6-6 7.9-11.3 7.9-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.1 6.2 29.3 4.2 24 4.2 13 4.2 4 13.2 4 24.2s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-4.1z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.1 6.2 29.3 4.2 24 4.2 16.3 4.2 9.7 8.5 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44.2c5.2 0 9.9-2 13.4-5.2l-6.2-5.1c-2 1.4-4.5 2.3-7.2 2.3-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.7 16.2 44.2 24 44.2z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.2 5.1c-.4.4 6.7-4.9 6.7-14.5 0-1.3-.1-2.7-.4-4z" />
    </svg>
  );
}
